import React, { useMemo, useRef, useEffect, useState } from "react";

function getHostEl() {
  return (
    document.querySelector("#nexus-search-widget") ||
    document.querySelector(".nexus-search-widget") ||
    document.querySelector("[data-nexus-search]")
  );
}

// Convert Font Awesome 6 syntax to Font Awesome 5 (for compatibility)
function normalizeFAFamily(family) {
  if (family === "fa-solid") return "fas";
  if (family === "fa-regular") return "far";
  if (family === "fa-brands") return "fab";
  // If already FA5 format, return as is
  if (family === "fas" || family === "far" || family === "fab") return family;
  // Default to solid
  return "fas";
}

function readLocationIconSpec() {
  const el = getHostEl();
  if (!el) {
    return { mode: "fa", faFamily: "fas", faIcon: "fa-location-dot", size: 16, color: "#0f172a", svg: "", show: true };
  }
  const mode = (el.getAttribute("data-location-icon-mode") || "fa").toLowerCase() === "svg" ? "svg" : "fa";
  const faFamilyRaw = el.getAttribute("data-location-icon-family") || "fa-solid";
  const faFamily = normalizeFAFamily(faFamilyRaw);
  const faIcon = el.getAttribute("data-location-icon-name") || "fa-location-dot";
  const size = parseInt(el.getAttribute("data-location-icon-size") || "16", 10) || 16;
  const color = el.getAttribute("data-location-icon-color") || "#0f172a";
  const svg = el.getAttribute("data-location-icon-svg") || "";
  const show = el.getAttribute("data-location-icon-show") !== "false";
  return { mode, faFamily, faIcon, size, color, svg, show };
}

function LocationIcon() {
  const [spec, setSpec] = useState(readLocationIconSpec);

  useEffect(() => {
    const el = getHostEl();
    if (!el) return;
    const sync = () => setSpec(readLocationIconSpec());
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type !== "attributes") continue;
        const n = m.attributeName || "";
        if (
          n === "data-location-icon-mode" ||
          n === "data-location-icon-family" ||
          n === "data-location-icon-name" ||
          n === "data-location-icon-size" ||
          n === "data-location-icon-color" ||
          n === "data-location-icon-svg" ||
          n === "data-location-icon-show"
        ) {
          sync();
        }
      }
    });
    mo.observe(el, { attributes: true });
    return () => mo.disconnect();
  }, []);

  if (!spec.show) return null;

  if (spec.mode === "svg" && spec.svg) {
    return (
      <span
        aria-hidden="true"
        style={{ display: "inline-grid", width: spec.size, height: spec.size, color: spec.color, placeItems: "center" }}
        dangerouslySetInnerHTML={{ __html: spec.svg }}
      />
    );
  }

  return (
    <i
      aria-hidden="true"
      className={`${spec.faFamily} ${spec.faIcon}`}
      style={{ fontSize: spec.size, color: spec.color, lineHeight: 1 }}
    />
  );
}

export default function LocationDropdown({
  palette,
  cities = [],
  selectedCity,
  setSelectedCity,
  fullWidth = true,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter cities based on search term
  const filteredCities = useMemo(() => {
    if (!searchTerm) return cities;
    return cities.filter(city =>
      city.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [cities, searchTerm]);

  // Get selected city label
  const locationLabel = useMemo(() => {
    const el = getHostEl();
    const defaultLabel = el?.getAttribute("data-location-label") || "Location";
    if (!selectedCity) return defaultLabel;
    const city = cities.find(c => c._id === selectedCity);
    return city ? city.name : defaultLabel;
  }, [selectedCity, cities]);

  // Handle ESC key to close modal
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  // ====== STYLES via CSS vars ======
  const rootStyle = {
    position: "relative",
    justifySelf: "end",
    width: fullWidth ? "100%" : "auto",
  };

  const pill = {
    display: "flex",
    alignItems: "center",
    gap: "var(--sw-pill-gap, 10px)",
    border: `1px solid var(--sw-pill-bc, ${palette.border})`,
    borderRadius: "var(--sw-pill-r, 10px)",
    padding: "var(--sw-pill-py, 10px) var(--sw-pill-px, 12px)",
    background: "var(--sw-pill-bg, transparent)",
    cursor: "pointer",
    userSelect: "none",
    justifyContent: "space-between",
    width: fullWidth ? "100%" : "auto",
    minWidth: "var(--sw-pill-minw, 150px)",
    height: "var(--sw-pill-h, 44px)",
    fontFamily: "var(--sw-pill-ff, inherit)",
    fontSize: "var(--sw-pill-fs, 14px)",
    fontWeight: "var(--sw-pill-fw, 600)",
    color: "var(--sw-pill-fc, inherit)",
    boxSizing: "border-box",
  };

  // Modal overlay
  const overlay = {
    position: "fixed",
    inset: 0,
    background: "var(--swm-overlay, rgba(0,0,0,.35))",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  // Modal panel
  const modalPanel = {
    background: "var(--swm-bg, #ffffff)",
    width: "var(--swm-w, 520px)",
    maxWidth: "95vw",
    maxHeight: "90vh",
    borderRadius: "var(--swm-r, 16px)",
    boxShadow: "var(--swm-shadow, 0 8px 24px rgba(2,6,23,.12))",
    border: "1px solid var(--swm-bc, rgba(15,23,42,.2))",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const modalHeader = {
    padding: "var(--swm-pad, 16px)",
    borderBottom: "1px solid var(--swm-div, rgba(148,163,184,.2))",
    fontFamily: "var(--swm-title-ff, inherit)",
    fontSize: "var(--swm-title-fs, 20px)",
    fontWeight: "var(--swm-title-fw, 800)",
    color: "var(--swm-title-fc, #0f172a)",
  };

  const modalBody = {
    padding: "var(--swm-pad, 16px)",
    overflowY: "auto",
    flex: 1,
  };

  const searchInput = {
    width: "100%",
    padding: "var(--sw-row-py, 10px)",
    border: `1px solid var(--sw-row-bc, rgba(148,163,184,.2))`,
    borderRadius: "var(--sw-ctr-radius, 8px)",
    fontFamily: "var(--sw-label-ff, inherit)",
    fontSize: "var(--sw-label-fs, 14px)",
    color: "var(--sw-label-fc, #0f172a)",
    outline: "none",
    marginBottom: "8px",
  };

  const listContainer = {
    overflowY: "auto",
    maxHeight: "300px",
    marginBottom: "8px",
  };

  const cityItem = (isSelected) => ({
    padding: "var(--sw-row-py, 10px)",
    cursor: "pointer",
    borderBottom: "1px solid var(--swm-list-bc, rgba(148,163,184,.2))",
    fontFamily: "var(--sw-label-ff, inherit)",
    fontSize: "var(--sw-label-fs, 14px)",
    fontWeight: isSelected ? "var(--sw-label-fw, 700)" : "var(--sw-sub-fw, 400)",
    color: isSelected ? "var(--swm-list-selected-fc, #0f172a)" : "var(--swm-list-fc, #0f172a)",
    background: isSelected ? "var(--swm-list-selected-bg, rgba(15,23,42,.08))" : "var(--swm-list-bg, transparent)",
    transition: "all 0.2s",
  });

  const cityItemHover = {
    background: "var(--swm-list-hover-bg, rgba(15,23,42,.05))",
    color: "var(--swm-list-hover-fc, #0f172a)",
  };

  const modalFooter = {
    padding: "var(--swm-pad, 16px)",
    borderTop: "1px solid var(--swm-div, rgba(148,163,184,.2))",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  };

  const cancelBtn = {
    width: "100%",
    borderRadius: "var(--sw-act-close-r, 10px)",
    border: `1px solid var(--sw-act-close-bc, ${palette.border})`,
    background: "var(--sw-act-close-bg, transparent)",
    color: "var(--sw-act-close-fc, inherit)",
    padding: "var(--sw-act-close-py, 10px) var(--sw-act-close-px, 12px)",
    cursor: "pointer",
    fontFamily: "var(--sw-act-close-ff, inherit)",
    fontWeight: "var(--sw-act-close-fw, 600)",
    fontSize: "var(--sw-act-close-fs, 14px)",
  };

  const applyBtn = {
    width: "100%",
    borderRadius: "var(--sw-act-apply-r, 10px)",
    border: `1px solid var(--sw-act-apply-bc, ${palette.border})`,
    background: "var(--sw-act-apply-bg, #f38b4a)",
    color: "var(--sw-act-apply-fc, #fff)",
    padding: "var(--sw-act-apply-py, 10px) var(--sw-act-apply-px, 12px)",
    cursor: "pointer",
    fontFamily: "var(--sw-act-apply-ff, inherit)",
    fontWeight: "var(--sw-act-apply-fw, 700)",
    fontSize: "var(--sw-act-apply-fs, 14px)",
  };

  const emptyMessage = {
    padding: "var(--sw-row-py, 10px)",
    textAlign: "center",
    fontFamily: "var(--sw-sub-ff, inherit)",
    fontSize: "var(--sw-sub-fs, 12px)",
    color: "var(--sw-sub-fc, #0f172aB3)",
  };

  const handleCitySelect = (cityId) => {
    setSelectedCity(cityId);
    setOpen(false);
    setSearchTerm("");
  };

  const handleClearSelection = () => {
    setSelectedCity(null);
    setOpen(false);
    setSearchTerm("");
  };

  return (
    <>
      <div style={rootStyle} data-button-type="pill">
        <div style={pill} onClick={() => setOpen((v) => !v)}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sw-pill-gap, 8px)" }}>
            <span aria-hidden="true">
              <LocationIcon />
            </span>
            <span>{locationLabel}</span>
          </span>
          <span aria-hidden="true">▾</span>
        </div>
      </div>

      {open && (
        <div style={overlay} onClick={() => { setOpen(false); setSearchTerm(""); }}>
          <style>{`
            .nexus-location-modal .nxsw-cancel-btn:hover {
              background: var(--swm-cancel-hover-bg, rgba(15,23,42,.05)) !important;
              color: var(--swm-cancel-hover-fc, #0f172a) !important;
              border-color: var(--swm-cancel-hover-bc, rgba(15,23,42,.3)) !important;
            }
            .nexus-location-modal .nxsw-apply-btn:hover {
              background: var(--swm-apply-hover-bg, #ea580c) !important;
              color: var(--swm-apply-hover-fc, #ffffff) !important;
              border-color: var(--swm-apply-hover-bc, transparent) !important;
            }
          `}</style>
          <div ref={dropRef} className="nexus-location-modal" style={modalPanel} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Location selector">
            {/* Modal Header */}
            <div style={modalHeader}>
              Select Location
            </div>

            {/* Modal Body */}
            <div style={modalBody}>
              <div style={listContainer}>
                {/* All option */}
                <div
                  style={cityItem(!selectedCity)}
                  onClick={handleClearSelection}
                  onMouseEnter={(e) => {
                    if (selectedCity) {
                      e.currentTarget.style.background = cityItemHover.background;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCity) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  All
                </div>

                {cities.length === 0 ? (
                  <div style={emptyMessage}>No locations found</div>
                ) : (
                  cities.map((city) => {
                    // Generate hierarchical prefix based on depth
                    // depth 0 = parent (no prefix), depth 1 = child ("-"), depth 2 = grandchild ("--"), etc.
                    const depth = city.depth || 0;
                    const prefix = depth > 0 ? "-".repeat(depth) + " " : "";

                    return (
                      <div
                        key={city._id}
                        style={cityItem(selectedCity === city._id)}
                        onClick={() => handleCitySelect(city._id)}
                        onMouseEnter={(e) => {
                          if (selectedCity !== city._id) {
                            e.currentTarget.style.background = cityItemHover.background;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedCity !== city._id) {
                            e.currentTarget.style.background = "transparent";
                          }
                        }}
                      >
                        {prefix}{city.name}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={modalFooter}>
              <button className="nxsw-cancel-btn" style={cancelBtn} onClick={() => { setOpen(false); setSearchTerm(""); }}>
                Cancel
              </button>
              <button className="nxsw-apply-btn" style={applyBtn} onClick={() => { setOpen(false); setSearchTerm(""); }}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
